import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { SidebarDrawerContext } from "@/components/SidebarDrawerContext";

import SuperAdminFooter from "./SuperAdminFooter";
import SuperAdminNavbar from "./SuperAdminNavbar";
import SuperAdminSidebar from "./SuperAdminSidebar";

/**
 * Layout for every super admin page: sidebar on the left, navbar / content /
 * footer stacked in the right column — the same shape as AppShell, including
 * the mobile drawer the sidebar folds into below `lg`.
 */
export default function SuperAdminShell({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();

  // Navigating from inside the drawer should leave it behind. Adjusted during
  // render rather than in an effect (react-hooks/set-state-in-effect): React
  // re-runs this component immediately with the new state, so the stale open
  // drawer is never committed — no flash, no cascading render.
  const [drawerPath, setDrawerPath] = useState(pathname);
  if (drawerPath !== pathname) {
    setDrawerPath(pathname);
    setDrawerOpen(false);
  }

  useEffect(() => {
    if (!drawerOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const close = useCallback(() => setDrawerOpen(false), []);
  const toggle = useCallback(() => setDrawerOpen((v) => !v), []);
  const drawer = useMemo(() => ({ open: drawerOpen, toggle, close }), [drawerOpen, toggle, close]);

  return (
    <SidebarDrawerContext.Provider value={drawer}>
      <div className="flex min-h-screen bg-background">
        <SuperAdminSidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <SuperAdminNavbar />
          <main className="min-w-0 flex-1">{children}</main>
          <SuperAdminFooter />
        </div>
      </div>
    </SidebarDrawerContext.Provider>
  );
}
